/** The person's saved searches, at the top of the Library's filter pane, and the dialog that names one. */
import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Bookmark, Pencil, Trash2 } from 'lucide-react';
import type { SavedSearchDTO, SavedSearchQuery } from '@prism/contracts';
import { api, errorMessage } from '../../api/client';
import { useSavedSearches } from '../../api/hooks';
import { queryKeys } from '../../api/queryKeys';
import { Modal } from '../../components/Modal';
import { toast } from '../../components/Toasts';

/** Open, rename, delete. */
export function SavedSearches({ onOpen }: { onOpen: (q: SavedSearchQuery) => void }) {
  const list = useSavedSearches();
  const qc = useQueryClient();
  const [renaming, setRenaming] = useState<SavedSearchDTO | null>(null);
  const items = list.data?.items ?? [];
  if (!items.length) return null;
  async function remove(s: SavedSearchDTO) {
    if (!window.confirm(`Delete the saved search “${s.name}”?`)) return;
    try {
      await api(`/api/v1/me/saved-searches/${s.id}`, { method: 'DELETE' });
      await qc.invalidateQueries({ queryKey: queryKeys.savedSearches() });
    } catch (err) {
      toast(errorMessage(err), 'error');
    }
  }
  return (
    <div className="facet" data-testid="saved-searches">
      <h3>Saved searches</h3>
      {items.map((s) => (
        <div key={s.id} className="saved-search">
          <button className="facet-option" onClick={() => onOpen(s.query)} title={`Open “${s.name}”`} data-testid="saved-search"><Bookmark size={12} /><span className="saved-search-name">{s.name}</span></button>
          <button className="btn ghost sm icon" aria-label={`Rename ${s.name}`} title="Rename" onClick={() => setRenaming(s)}><Pencil size={12} /></button>
          <button className="btn ghost sm icon danger" aria-label={`Delete ${s.name}`} title="Delete" onClick={() => void remove(s)}><Trash2 size={12} /></button>
        </div>
      ))}
      {renaming && <SaveSearchDialog existing={renaming} onClose={() => setRenaming(null)} />}
    </div>
  );
}

/** Name a search to keep it (new), or rename a saved one. */
export function SaveSearchDialog({ query, existing, onClose }: { query?: SavedSearchQuery; existing?: SavedSearchDTO; onClose: () => void }) {
  const qc = useQueryClient();
  const [name, setName] = useState(existing?.name ?? '');
  const [busy, setBusy] = useState(false);
  async function save() {
    if (!name.trim()) return;
    setBusy(true);
    try {
      if (existing) await api(`/api/v1/me/saved-searches/${existing.id}`, { method: 'PUT', json: { name: name.trim() } });
      else await api('/api/v1/me/saved-searches', { method: 'POST', json: { name: name.trim(), query } });
      await qc.invalidateQueries({ queryKey: queryKeys.savedSearches() });
      toast(existing ? 'Renamed' : `Saved “${name.trim()}”`);
      onClose();
    } catch (err) {
      toast(errorMessage(err), 'error');
      setBusy(false);
    }
  }
  return (
    <Modal title={existing ? 'Rename saved search' : 'Save search'} onClose={onClose}
      actions={<><button className="btn ghost" onClick={onClose}>Cancel</button><button className="btn primary" disabled={busy || !name.trim()} onClick={() => void save()} data-testid="save-search-confirm">Save</button></>}>
      <div className="field">
        <label htmlFor="saved-search-name">Name</label>
        <input id="saved-search-name" className="input" autoFocus value={name} placeholder="Harbour footage this week" maxLength={120}
          onChange={(e) => setName(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && void save()} data-testid="save-search-name" />
        {!existing && <span className="help">Keeps the search text, filters, advanced search, sort and view. Only you see your saved searches.</span>}
      </div>
    </Modal>
  );
}
